import React from 'react';
import { normalizePriceRuleSelection, resolvePriceRuleSelection } from '../../utils/complexPriceRules';
import { getQuickRuleEquivalent, getQuickRuleReferenceCost, getQuickRuleMargin, getQuickRuleBadgeMargin, formatSignedRuleValue, formatRuleMarginAmount } from './priceRuleEquivalence';
import { formatMoney } from './quickPriceListModel';

export const summarizeRuleEquivalents = (formats, complexPriceRules, { both = false, badge = false } = {}) => {
  const groups = new Map();
  let unavailable = 0;
  for (const format of formats) {
    const selection = normalizePriceRuleSelection(format.priceRule, format.stockPriceRule);
    const basis = selection?.rule?.basis || format.analysis?.pricingBasis || 'unit';
    const cost = getQuickRuleReferenceCost(format, basis);
    const resolved = format.analysis?.priceRule?.resolvedRule
      || resolvePriceRuleSelection(selection, { complexPriceRules, basis, referenceCost: cost }).resolvedRule;
    const equivalent = getQuickRuleEquivalent(resolved, cost);
    const margin = badge ? getQuickRuleBadgeMargin(resolved, cost) : getQuickRuleMargin(resolved, cost);
    if (badge ? !margin : !equivalent || equivalent.unavailable) { unavailable += 1; continue; }
    const values = both || badge
      ? [{ mode: 'percent', value: margin.percent }, { mode: 'amount', value: margin.amount }]
      : [equivalent];
    for (const entry of values) {
      if (entry.value === null) continue;
      if (badge && entry.mode === 'amount' && resolved.mode !== 'markup_amount') continue;
      const key = `${entry.mode}:${basis}`;
      const current = groups.get(key) || { mode: entry.mode, basis, min: entry.value, max: entry.value };
      current.min = Math.min(current.min, entry.value);
      current.max = Math.max(current.max, entry.value);
      if (badge) {
        current.count = (current.count || 0) + 1;
        current.mean = (current.mean || 0) + (entry.value - (current.mean || 0)) / current.count;
      }
      groups.set(key, current);
    }
  }
  return { ranges: [...groups.values()], unavailable };
};

const SharedRuleEquivalents = ({ formats, complexPriceRules }) => {
  const { ranges, unavailable } = summarizeRuleEquivalents(formats, complexPriceRules, { both: true });
  const labels = [...ranges].sort((left, right) => Number(left.mode === 'percent') - Number(right.mode === 'percent')).map(({ mode, basis, min, max }) => {
    const label = (value) => mode === 'amount' ? formatMoney(value)
      : `${value.toLocaleString('es-CL', { maximumFractionDigits: 2 })}%`;
    return `${mode === 'percent' ? '≈ ' : ''}${label(min)}${min === max ? '' : ` a ${label(max)}`}${mode === 'amount' && basis === 'kg' ? ' / kg' : ''}`;
  });
  return <p className="mt-1 text-[11px] leading-tight text-slate-500"
    title="Equivalentes según el costo de los formatos visibles, antes del redondeo del precio final.">
    Equivalente: {labels.length ? labels.join(' · ') : 'pendiente de costo o tramo válido.'}
    {labels.length > 0 && unavailable > 0 ? ` · ${unavailable} sin equivalente calculable.` : ''}
  </p>;
};

export default SharedRuleEquivalents;

export const sharedMarginRuleLabel = (formats, complexPriceRules) => {
  const { ranges } = summarizeRuleEquivalents(formats, complexPriceRules, { badge: true });
  if (!ranges.length) return 'Pendiente';
  const percentages = ranges.filter(({ mode }) => mode === 'percent');
  const count = percentages.reduce((total, range) => total + range.count, 0);
  const mean = percentages.reduce((total, range) => total + range.mean * (range.count / count), 0);
  const fixed = percentages.every(({ min, max }) => min === max && min === percentages[0].min);
  const amounts = ranges.filter(({ mode }) => mode === 'amount').map(({ basis, min, max }) => {
    const label = `${formatRuleMarginAmount(min)}${min === max ? '' : ` a ${formatRuleMarginAmount(max)}`}`;
    return `${label}${basis === 'kg' ? ' / kg' : percentages.length > 1 ? ' / unidad' : ''}`;
  });
  const percent = formatSignedRuleValue(mean, 'percent', fixed ? 3 : 2);
  return amounts.length ? `${amounts.join(' · ')} ≈ ${percent}` : `${fixed ? '' : '≈ '}${percent}`;
};
